import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ─── Shared Building Config ───────────────────────────────────────────────────
export const BUILDINGS_CONFIG = {
  F1: {
    floors: [
      { floor: 0, label: 'Ground Floor', rooms: Array.from({ length: 16 }, (_, i) => `F1-${String(i + 1)?.padStart(3, '0')}`) },
      ...[1, 2, 3, 4]?.map(f => ({ floor: f, label: `Floor ${f}`, rooms: Array.from({ length: 20 }, (_, i) => `F1-${f}${String(i + 1)?.padStart(2, '0')}`) }))
    ]
  },
  F2: {
    floors: [
      { floor: 0, label: 'Ground Floor', rooms: Array.from({ length: 16 }, (_, i) => `F2-${String(i + 1)?.padStart(3, '0')}`) },
      ...[1, 2, 3, 4]?.map(f => ({ floor: f, label: `Floor ${f}`, rooms: Array.from({ length: 20 }, (_, i) => `F2-${f}${String(i + 1)?.padStart(2, '0')}`) }))
    ]
  },
  G1: {
    floors: [
      { floor: 0, label: 'Ground Floor', rooms: Array.from({ length: 16 }, (_, i) => `G1-${String(i + 1)?.padStart(3, '0')}`) },
      ...[1, 2, 3, 4]?.map(f => ({ floor: f, label: `Floor ${f}`, rooms: Array.from({ length: 20 }, (_, i) => `G1-${f}${String(i + 1)?.padStart(2, '0')}`) }))
    ]
  },
  G2: {
    floors: [
      { floor: 0, label: 'Ground Floor', rooms: Array.from({ length: 16 }, (_, i) => `G2-${String(i + 1)?.padStart(3, '0')}`) },
      ...[1, 2, 3, 4]?.map(f => ({ floor: f, label: `Floor ${f}`, rooms: Array.from({ length: 20 }, (_, i) => `G2-${f}${String(i + 1)?.padStart(2, '0')}`) }))
    ]
  },
  H: {
    floors: [
      { floor: 0, label: 'Ground Floor', rooms: ['H-001','H-002','H-003','H-004','H-005','H-006','H-007','H-008','H-009','H-010'] },
      ...[1, 2, 3, 4]?.map(f => ({ floor: f, label: `Floor ${f}`, rooms: Array.from({ length: 7 }, (_, i) => `H-${f}${String(i + 1)?.padStart(2, '0')}`) }))
    ]
  },
  L: {
    floors: [
      { floor: 0, label: 'Ground Floor', rooms: ['L-001','L-002','L-003'] },
      { floor: 1, label: 'Floor 1', rooms: ['L-101','L-102','L-103'] },
      { floor: 2, label: 'Floor 2', rooms: ['L-201','L-202','L-203'] },
      { floor: 3, label: 'Floor 3', rooms: ['L-301','L-302'] },
      { floor: 4, label: 'Floor 4', rooms: ['L-401','L-402'] },
    ]
  }
};

// ─── Reservation Status Constants ─────────────────────────────────────────────
// active-reservation : Admin approved the request (awaiting check-in)
// confirmed-stay     : Admin performed check-in (guest is in the room)
// completed-stay     : Admin performed check-out (stay is finished)
export const RESERVATION_STATUS = {
  ACTIVE_RESERVATION: 'active-reservation',
  CONFIRMED_STAY: 'confirmed-stay',
  COMPLETED_STAY: 'completed-stay',
};

// ─── Initial Stays ────────────────────────────────────────────────────────────
const INITIAL_STAYS = [];

// ─── Build initial rooms list ─────────────────────────────────────────────────
let idCounter = 1;
const INITIAL_ROOMS = Object.entries(BUILDINGS_CONFIG)?.flatMap(([building, cfg]) =>
  cfg?.floors?.flatMap(({ floor, rooms }) =>
    rooms?.map(roomNumber => ({
      id: idCounter++,
      roomNumber,
      building,
      floor,
      type: building === 'L' ? 'Apartment' : floor === 0 ? 'Studio' : 'Standard',
      capacity: building === 'L' ? 2 : 1,
      notes: '',
    }))
  )
);

// ─── Context ──────────────────────────────────────────────────────────────────
const BookingContext = createContext(null);

export function BookingProvider({ children }) {
  const [stays, setStays] = useState(INITIAL_STAYS);
  const [rooms, setRooms] = useState(INITIAL_ROOMS);
  // Manual overrides for cleaning status (admin can set 'cleaning-in-progress' or 'cleaned')
  const [manualCleaningStatus, setManualCleaningStatus] = useState({});

  // ── Derived room status ──
  // Room is "occupied" only when a guest has confirmed-stay (checked-in)
  const getRoomStatus = useCallback((roomNumber) => {
    const manual = manualCleaningStatus?.[roomNumber];
    if (manual === 'cleaning-in-progress') return 'cleaning-in-progress';
    if (manual === 'cleaned') return 'cleaned';
    // Confirmed stay = guest is physically in the room → Occupied
    const confirmedStay = stays?.find(s => s?.room === roomNumber && s?.status === 'confirmed-stay');
    if (confirmedStay) return 'occupied';
    // Active reservation = room is reserved but guest hasn't arrived yet
    const activeReservation = stays?.find(s => s?.room === roomNumber && s?.status === 'active-reservation');
    if (activeReservation) return 'reserved';
    const recentCheckout = stays?.find(s => s?.room === roomNumber && s?.status === 'completed-stay');
    if (recentCheckout) return 'needs-housekeeping';
    return 'available';
  }, [stays, manualCleaningStatus]);

  // ── Add a new check-in (manual check-in from Check-in tab) ──
  // Creates a confirmed-stay directly since admin is performing the physical check-in
  const addCheckIn = useCallback((stayData) => {
    const newStay = {
      id: Date.now(),
      ...stayData,
      status: 'confirmed-stay',
      guestStatus: 'checked-in',
    };
    setStays(prev => [newStay, ...prev]);
    // Clear any manual cleaning status for this room (it's now occupied)
    setManualCleaningStatus(prev => {
      const next = { ...prev };
      delete next?.[stayData?.room];
      return next;
    });
    return newStay;
  }, []);

  // ── Add approved reservation (from reservation request approval) ──
  // Status = "Active Reservation" — guest has been approved but not yet checked in
  const addApprovedReservation = useCallback((requestData) => {
    const building = requestData?.preferred_building
      ? requestData?.preferred_building?.split('-')?.[0] || requestData?.preferred_building
      : 'F1';

    const allRoomsInBuilding = BUILDINGS_CONFIG?.[building]?.floors?.flatMap(f => f?.rooms) || [];
    const occupiedRooms = new Set(
      stays
        .filter(s => {
          if (s.status === 'completed-stay') return false;
          const sIn = s.checkIn;
          const sOut = s.checkOut;
          const rIn = requestData.requested_check_in;
          const rOut = requestData.requested_check_out;
          return sIn < rOut && sOut > rIn;
        })
        .map(s => s.room)
    );
    const availableRoom = allRoomsInBuilding?.find(r => !occupiedRooms?.has(r));
    const assignedRoom = availableRoom || (allRoomsInBuilding?.[0] || `${building}-101`);

    const newStay = {
      id: Date.now(),
      residentName: requestData?.full_name || 'Guest',
      residentId: `REQ-${requestData?.id?.slice(0, 8) || Date.now()}`,
      email: requestData?.email || '',
      room: assignedRoom,
      checkIn: requestData?.requested_check_in,
      checkOut: requestData?.requested_check_out,
      status: 'active-reservation',
      building,
      accommodationType: 'short-term',
      guestStatus: 'awaiting-arrival',
      fromReservationRequest: true,
      reservationRequestId: requestData?.id,
      department: requestData?.department || '',
      financialStatus: requestData?.financial_status || '',
      phone: requestData?.phone || '',
      nationality: requestData?.nationality || '',
    };

    setStays(prev => [newStay, ...prev]);
    setManualCleaningStatus(prev => {
      const next = { ...prev };
      delete next?.[assignedRoom];
      return next;
    });
    return newStay;
  }, [stays]);

  // ── Perform check-in on an existing active-reservation ──
  // Changes status from "active-reservation" → "confirmed-stay", room becomes Occupied
  const performCheckIn = useCallback((stayId) => {
    let checkedInRoom = null;
    setStays(prev => prev?.map(s => {
      if (s?.id === Number(stayId)) {
        checkedInRoom = s?.room;
        return { ...s, status: 'confirmed-stay', guestStatus: 'checked-in' };
      }
      return s;
    }));
    if (checkedInRoom) {
      setManualCleaningStatus(prev => {
        const next = { ...prev };
        delete next?.[checkedInRoom];
        return next;
      });
    }
  }, []);

  // ── Process check-out ──
  // Changes status to "completed-stay"
  const processCheckOut = useCallback((stayIdOrObj, actualCheckOut) => {
    const stayId = typeof stayIdOrObj === 'object' ? stayIdOrObj?.id : stayIdOrObj;
    const checkOutDate = actualCheckOut || (typeof stayIdOrObj === 'object' ? stayIdOrObj?.actualCheckOut : null);
    let checkedOutRoom = null;
    setStays(prev => prev?.map(s => {
      if (s?.id === Number(stayId)) {
        checkedOutRoom = s?.room;
        return { ...s, status: 'completed-stay', checkOut: checkOutDate || s?.checkOut, guestStatus: 'checked-out' };
      }
      return s;
    }));
    if (checkedOutRoom) {
      setManualCleaningStatus(prev => {
        const next = { ...prev };
        delete next?.[checkedOutRoom];
        return next;
      });
    }
  }, []);

  // ── Update stay (from room management) ──
  const updateStayRoom = useCallback((stayId, newRoom, newBuilding) => {
    setStays(prev => prev?.map(s =>
      s?.id === stayId ? { ...s, room: newRoom, building: newBuilding } : s
    ));
  }, []);

  // ── Room CRUD ──
  const addRoom = useCallback((roomData) => {
    setRooms(prev => [...prev, { id: Date.now(), ...roomData }]);
  }, []);

  const updateRoom = useCallback((roomId, roomData) => {
    setRooms(prev => prev?.map(r => r?.id === roomId ? { ...r, ...roomData } : r));
  }, []);

  const deleteRoom = useCallback((roomId) => {
    setRooms(prev => prev?.filter(r => r?.id !== roomId));
  }, []);

  // ── Housekeeping actions ──
  const setCleaningStatus = useCallback((roomNumber, status) => {
    if (status === 'available') {
      setManualCleaningStatus(prev => {
        const next = { ...prev };
        delete next?.[roomNumber];
        return next;
      });
    } else {
      setManualCleaningStatus(prev => ({ ...prev, [roomNumber]: status }));
    }
  }, []);

  // ── Computed stats ──
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const todayCheckOuts = stays?.filter(s => s?.status === 'completed-stay' && s?.checkOut === today);

  const roomsNeedingHousekeeping = rooms?.filter(r => {
    const status = getRoomStatus(r?.roomNumber);
    return status === 'needs-housekeeping';
  });

  const roomsCleaningInProgress = rooms?.filter(r => {
    const status = getRoomStatus(r?.roomNumber);
    return status === 'cleaning-in-progress';
  });

  return (
    <BookingContext.Provider value={{
      stays,
      rooms,
      manualCleaningStatus,
      getRoomStatus,
      addCheckIn,
      addApprovedReservation,
      performCheckIn,
      processCheckOut,
      updateStayRoom,
      addRoom,
      updateRoom,
      deleteRoom,
      setCleaningStatus,
      todayCheckOuts,
      roomsNeedingHousekeeping,
      roomsCleaningInProgress,
      today,
    }}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error('useBooking must be used within BookingProvider');
  return ctx;
}
